import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    ButtonGroup,
    Divider,
    Flex,
    HStack,
    Icon,
    Text,
    VStack,
    useColorMode
} from '@chakra-ui/react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaSpinner } from 'react-icons/fa';

const filters = [
    { key: 'all', label: 'All' },
    { key: 'conversation', label: 'Conversation' },
    { key: 'actions', label: 'Actions' },
    { key: 'system', label: 'System' }
];

const formatElapsed = (elapsedMs = 0) => {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
};

const getStatusIcon = (status) => {
    if (status === 'failed') return FaExclamationCircle;
    if (status === 'completed') return FaCheckCircle;
    if (status === 'running') return FaSpinner;
    return FaInfoCircle;
};

const getStatusColor = (status) => {
    if (status === 'failed') return 'red';
    if (status === 'completed') return 'green';
    if (status === 'running') return 'blue';
    return 'gray';
};

const LiveCallActivityTimeline = ({ liveActivityUrl, onStopMonitoring }) => {
    const { colorMode } = useColorMode();
    const [events, setEvents] = useState([]);
    const [filter, setFilter] = useState('all');
    const [connectionState, setConnectionState] = useState(liveActivityUrl ? 'connecting' : 'idle');
    const listRef = useRef(null);
    const shouldAutoScroll = useRef(true);

    useEffect(() => {
        if (!liveActivityUrl) {
            setConnectionState('idle');
            return undefined;
        }

        setConnectionState('connecting');
        const eventSource = new EventSource(liveActivityUrl);

        eventSource.onopen = () => setConnectionState('connected');
        eventSource.onerror = () => setConnectionState('error');
        eventSource.addEventListener('activity', (message) => {
            const event = JSON.parse(message.data);
            setEvents(prev => {
                if (prev.some(item => item.id === event.id)) return prev;
                return [...prev, event];
            });
        });
        eventSource.addEventListener('close', () => {
            setConnectionState('ended');
            eventSource.close();
        });

        return () => {
            eventSource.close();
        };
    }, [liveActivityUrl]);

    useEffect(() => {
        if (!shouldAutoScroll.current || !listRef.current) return;
        listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [events]);

    const visibleEvents = useMemo(() => {
        if (filter === 'all') return events;
        return events.filter(event => event.category === filter);
    }, [events, filter]);

    const handleScroll = () => {
        const el = listRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        shouldAutoScroll.current = distanceFromBottom < 32;
    };

    return (
        <Box>
            <Flex justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={3} direction={{ base: 'column', md: 'row' }}>
                <Box>
                    <HStack spacing={2}>
                        <Text fontWeight="semibold">Live Activity</Text>
                        <Badge colorScheme={connectionState === 'connected' ? 'green' : connectionState === 'error' ? 'red' : 'gray'}>
                            {connectionState}
                        </Badge>
                    </HStack>
                    <Text fontSize="sm" color={colorMode === 'light' ? 'gray.600' : 'gray.300'}>
                        Realtime transcript and assistant actions for this test call.
                    </Text>
                </Box>
                <Button size="sm" variant="outline" onClick={onStopMonitoring}>
                    Stop Monitoring
                </Button>
            </Flex>

            <ButtonGroup size="sm" isAttached mt={4} flexWrap="wrap">
                {filters.map(item => (
                    <Button
                        key={item.key}
                        variant={filter === item.key ? 'solid' : 'outline'}
                        colorScheme={filter === item.key ? 'blue' : 'gray'}
                        onClick={() => setFilter(item.key)}
                    >
                        {item.label}
                    </Button>
                ))}
            </ButtonGroup>

            <Divider my={4} />

            <VStack
                ref={listRef}
                align="stretch"
                spacing={3}
                maxH="420px"
                overflowY="auto"
                onScroll={handleScroll}
                pr={1}
            >
                {visibleEvents.length === 0 ? (
                    <Box py={10} textAlign="center" color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>
                        <Text fontWeight="medium">Waiting for activity...</Text>
                        <Text fontSize="sm">Events will appear here as the test call runs.</Text>
                    </Box>
                ) : visibleEvents.map(event => {
                    const color = getStatusColor(event.status);
                    const StatusIcon = getStatusIcon(event.status);

                    return (
                        <Box
                            key={event.id}
                            borderWidth="1px"
                            borderColor={colorMode === 'light' ? 'gray.200' : 'gray.600'}
                            borderRadius="md"
                            p={3}
                            bg={colorMode === 'light' ? 'white' : 'gray.700'}
                        >
                            <Flex justify="space-between" gap={3} align="start">
                                <HStack align="start" spacing={3}>
                                    <Icon as={StatusIcon} color={`${color}.400`} mt={1} />
                                    <Box>
                                        <HStack spacing={2} flexWrap="wrap">
                                            <Text fontWeight="semibold">{event.title}</Text>
                                            <Badge colorScheme={color}>{event.source}</Badge>
                                            <Text fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.300'}>
                                                {formatElapsed(event.elapsedMs)}
                                            </Text>
                                        </HStack>
                                        {event.message && (
                                            <Text mt={2} fontSize="sm" whiteSpace="pre-wrap">
                                                {event.message}
                                            </Text>
                                        )}
                                        {event.durationMs != null && (
                                            <Text mt={2} fontSize="xs" color={colorMode === 'light' ? 'gray.500' : 'gray.300'}>
                                                Completed in {event.durationMs}ms
                                            </Text>
                                        )}
                                    </Box>
                                </HStack>
                            </Flex>
                        </Box>
                    );
                })}
            </VStack>
        </Box>
    );
};

export default LiveCallActivityTimeline;
